import React, { lazy, Suspense } from "react";
import { Switch, withRouter, Route } from "react-router-dom";
import * as styles from "./app.styles";
import { ClearStorage, withRedux } from "helpers";

// Auth stuff
import AuthRoute from "./pages/Authentication/AuthRoute";
import PublicRoute from "./pages/Authentication/PublicRoute";
import ProtectedRoute from "./pages/Authentication/ProtectedRoute";
import LoginSignup from "./pages/Authentication/LoginSignup";
import ForgotPassword from "./pages/Authentication/ForgotPassword";
import ResetPassword from "./pages/Authentication/ResetPassword";
import Terms from "./pages/LegalDocs/Terms";
import PrivacyPolicy from "./pages/LegalDocs/PrivacyPolicy";
import NewAppBusiness from "./pages/NewAppBusiness";
import Shippo from "./pages/Shippo";
import HowItWorks from "./pages/HowItWorks";
import OurMission from "./pages/OurMission";
import SupportSmallBiz from "./pages/SupportSmallBusiness";

// new designed components
import NewLanding from "./pages/NewLanding";
import MarketingLandingPage from "./pages/MarketingLandingPage";
import AppleAppSite from "./pages/AppleAppSite";
import Pricing from "./pages/Pricing";
import MarketPlace from "./pages/MarketPlace";
import MarketPlacePower from "./pages/MarketPlacePower";
import Newsletters from "./pages/Newsletters";
import NoMatch from "./pages/NoMatch";
import Contact from "./pages/Contact";
import Checkout from "./pages/Checkout";
import Tutorials from "./pages/Tutorials";
import OurStory from "./pages/OurStory";
import Stores from "./pages/Stores";
import Store from "./pages/Analytics/Orders/Store";
import Customers from "./pages/Analytics/Customers";
import Coupons from "./pages/Coupons";
import Invoices from "./pages/Invoices";
import ReadOnlyInvoiceDetails from "./pages/Invoices/ReadOnlyInvoiceDetails";
import InvoiceDetails from "./pages/Invoices/InvoiceDetails";
import StoreSettings from "./pages/StoreSettings";
import FAQs from "./pages/FAQs";
import Partners from "./pages/Partners";

// Web Feature
import { HomeWebComponent } from "./pages/Web";

// App stuff
import Home from "./pages/Home";
import Nav from "./pages/Nav";
import NewNavBar from "./pages/NewNavBar";
import Lobby from "./pages/Lobby";
import { NProgress, Error } from "components";

// const Lobby = lazy(() =>
//   import(/* webpackChunkName: "Lobby" */ "./pages/Lobby")
// );
// const Home = lazy(() => import(/* webpackChunkName: "Home" */ "./pages/Home"));

// SEO
import { Helmet } from "react-helmet";

import useScrollTrigger from "./pages/NewNavBar/useScrollTrigger";
import { createMuiTheme, ThemeProvider } from "@material-ui/core/styles";

const theme = createMuiTheme({
  typography: {
    fontFamily:
      "Lucida Grande, Lucida Sans Unicode, Lucida Sans, Geneva, Verdana, sans-serif",
  },
  palette: {
    primary: {
      main: "#89d2d3",
    },
    secondary: {
      main: "#465362",
    },
  },
  overrides: {
    // Style sheet name ⚛️
    MuiButton: {
      // Name of the rule
      containedPrimary: {
        // Some CSS
        color: "white",
      },
      root: {
        fontFamily:
          "Lucida Grande, Lucida Sans Unicode, Lucida Sans, Geneva, Verdana, sans-serif",
      },
    },
    MuiListItemText: {
      primary: {
        fontFamily: "Lucida Grande",
        fontSize: "0.9375rem",
      },
    },
    MuiSwitch: {
      colorPrimary: "#89d2d3",
    },
  },
});

const newDesignRoutes = [
  "/",
  "/pricing",
  "/contact",
  "/tutorials",
  "/forgot-password",
  "/reset-password",
  "/terms",
  "/privacy_policy",
  "/:app",
  "/:app/home",
  "/invoices?id=",
];

const internalAppRoutes = [
  "/checkout",
  "/lobby",
  "/application",
  "/orders",
  "/coupons",
  "/store-settings",
  "/form/business",
  "/user-tutorials",
];

function Routes(props) {
  // console.log("routes got props");
  // console.dir(props);
  const {
    platformInfo: { status: user },
  } = props;
  // console.dir(user.user);
  const currentPath = window.location.pathname;
  // console.dir(window.location);
  // console.log(currentPath.includes("customers"));

  const [trigger, setRef] = useScrollTrigger({
    threshold: 300,
    disableHysteresis: true,
  });

  const isNewDesignRoute = newDesignRoutes.some(
    (route) => currentPath === route
  );
  const isInternalAppRoute = internalAppRoutes.some(
    (route) => currentPath === route
  );

  const isAccessCodeCompatible =
    (currentPath.includes("customers") &&
      !currentPath.includes("accessCode") &&
      user.user) ||
    (currentPath.includes("invoices") &&
      !currentPath.includes("accessCode") &&
      user.user);

  // console.log(currentPath);

  let hideNav = false;

  if (window.location.search) {
    // console.log(window.location.search);
    if (
      window.location.search.includes("?id=") &&
      currentPath.includes("invoices")
    ) {
      // console.log("^^^^^LOADED A UUID PAGE");
      hideNav = true;
    }
  }

  return (
    <div className={styles.app}>
      <Helmet>
        <title>Mobile360 | Mobile App Builder</title>
        <meta
          name="description"
          content="Mobile360, the go to 30 minute no code mobile app builder."
        />
        <meta
          name="keywords"
          content="mobile, app development, small business, business, coding, android, ios, react native, marketing, customers, growth, free, best, design, mobile apps, apps, mobile360, thunkable, appypie, iphone, apple, google, DYI app maker, DYI app builder, appery.io"
        />
        <link rel="canonical" href="https://www.mobile360.io/" />
        <meta property="og:url" content="https://www.mobile360.io/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Mobile 360" />
        <meta
          property="og:description"
          content="Mobile360, the go to 30 minute no code mobile app builder."
        />
        <meta
          property="og:image"
          content="https://mobile360-assets.s3.us-east-2.amazonaws.com/landing/Hero Image.svg"
        />
        <meta property="og:image:type" content="image/svg+xml" />
        <meta property="og:image:height" content="1200" />
        <meta property="og:image:width" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:image"
          content="https://mobile360-assets.s3.us-east-2.amazonaws.com/landing/Hero Image.svg"
        />
        <meta name="twitter:creator" content="@Mobile360_io" />
        <meta name="twitter:site" content="@Mobile360_io" />
      </Helmet>
      <ThemeProvider theme={theme}>
        <ClearStorage />
        <NProgress />
        <Error />

        {(isInternalAppRoute || isAccessCodeCompatible) && !hideNav && (
          <Route path="/(.+)" component={Nav} />
        )}

        <div
          ref={setRef}
          className={isInternalAppRoute ? styles.container : null}
        >
          <Switch>
            <AuthRoute exact path="/" component={NewLanding} />
            <AuthRoute
              exact
              path="/apple-app-site-association"
              component={AppleAppSite}
            />
            <AuthRoute exact path="/fbpromo" component={MarketingLandingPage} />
            <AuthRoute exact path="/pricing" component={Pricing} />
            <AuthRoute exact path="/how-it-works" component={HowItWorks} />
            <AuthRoute
              exact
              path="/support-small-business"
              component={SupportSmallBiz}
            />
            <AuthRoute exact path="/our-mission" component={OurMission} />
            <AuthRoute exact path="/newsletters" component={Newsletters} />

            <AuthRoute exact path="/ourstory" component={OurStory} />
            <AuthRoute exact path="/marketplace" component={MarketPlace} />
            <AuthRoute
              exact
              path="/power-of-marketplace"
              component={MarketPlacePower}
            />
            <AuthRoute exact path="/faqs" component={FAQs} />
            <AuthRoute exact path="/contact" component={Contact} />
            <AuthRoute exact path="/tutorials" component={Tutorials} />
            <AuthRoute exact path="/partners" component={Partners} />
            <AuthRoute
              exact
              path="/forgot-password"
              component={ForgotPassword}
            />
            <AuthRoute exact path="/reset-password" component={ResetPassword} />
            <AuthRoute
              exact
              path="/acceptInvitation"
              component={LoginSignup}
              hide={false}
              isWeb={false}
            />
            <Route exact path="/privacy_policy" component={PrivacyPolicy} />
            <Route exact path="/terms" component={Terms} />
            <ProtectedRoute exact path="/checkout" component={Checkout} />
            {/* <Suspense fallback={<div>Loading...</div>}> */}
            <ProtectedRoute exact path="/lobby" component={Lobby} />
            <ProtectedRoute exact path="/application" component={Home} />
            {/* </Suspense> */}

            <ProtectedRoute
              exact
              path="/user-tutorials"
              component={Tutorials}
            />
            <ProtectedRoute exact path="/orders" component={Store} />

            <ProtectedRoute exact path="/coupons" component={Coupons} />
            <ProtectedRoute
              exact
              path="/store-settings"
              component={StoreSettings}
            />
            <ProtectedRoute
              exact
              path="/form/business"
              component={NewAppBusiness}
            />
            <ProtectedRoute path="/shippo-oauth-redirect/" component={Shippo} />
            {/* app/{appId}/admin/invoices?accessToken=${token */}
            {/* http://localhost:3300/app/4405/admin/invoices?accessToken=eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJqYWd0ZWpzYkB5YWhvby5jb20iLCJleHAiOjE2NzEzMzkxMTN9.w-EMEZ5GVbtlwAUgU-uXpQd50NtuAfJiY29LLTTayc0tzlon7gETxGiOkUvEtw_bL1BgFo4fqt1eql-TJqK-2g */}
            <PublicRoute
              exact
              path="/app/:appId/admin/invoices"
              component={Invoices}
            />
            {/* http://localhost:3300/invoices?id=w17o-1acfcaa3779146519c5f94137c23f256 */}
            <PublicRoute exact path="/invoices" component={InvoiceDetails} />
            <PublicRoute
              exact
              path="/:appName/invoices"
              component={ReadOnlyInvoiceDetails}
            />
            <PublicRoute exact path="/:appId/customers" component={Customers} />
            <PublicRoute exact path="/:appId/stores" component={Stores} />
            <PublicRoute exact path="/:app" component={NoMatch} />
            <PublicRoute exact path="/:app/home" component={HomeWebComponent} />
            <PublicRoute
              exact
              path="/:app/home?isAdmin=true"
              component={HomeWebComponent}
            />
            <Route component={NoMatch} />
          </Switch>
        </div>
      </ThemeProvider>
    </div>
  );
}

export default withRedux(withRouter(Routes));
